import React, { Component } from "react";
import equal from "fast-deep-equal";
import { observer } from "mobx-react";

import { clone } from "@formant/core/common/clone";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { Fader } from "@formant/ui/component/common/Fader";
import { HStack } from "@formant/ui/component/common/HStack";

import { operateStore } from "../../../stores";
import { Buttons } from "../../common/Buttons";
import { Popup } from "../../common/Popup";
import { StandardButton } from "../../common/StandardButton";
import { TextButton } from "../../common/TextButton";
import { WarningMessage } from "../../common/WarningMessage";
import { DefaultTeleopConfiguration } from "../../configuration/DefaultTeleopConfiguration";
import { RealtimeTelemetryConfiguration } from "../../configuration/RealtimeTelemetryConfiguration";
import { TeleopControlsConfiguration } from "../../configuration/TeleopControlsConfiguration";
import * as styles from "./ConfigurationPopup.scss";

export interface IConfigurationPopupProps {
    device: IDevice;
    document: IDeviceConfigurationDocument;
}

@observer
export class ConfigurationPopup extends Component<IConfigurationPopupProps> {
    public render() {
        const { device } = this.props;
        const { session, editingDevice } = operateStore;
        const { needsRestart } = this;

        if (!session || !session.deviceConfiguration) {
            return false;
        }

        return (
            <Popup
                visible={editingDevice}
                windowClassName={styles["configuration-popup"]}
                title="Teleop settings"
            >
                <TeleopControlsConfiguration
                    devices={[device]}
                    document={session.deviceConfiguration?.document}
                    dirty={false}
                    validation={{}}
                    onChange={this.onChangeDeviceConfiguration}
                    onSave={this.onSaveDeviceConfiguration}
                    loading={!session?.deviceConfiguration?.document}
                    popup={true}
                />
                <RealtimeTelemetryConfiguration
                    devices={[device]}
                    document={session.deviceConfiguration?.document}
                    dirty={false}
                    validation={{}}
                    onChange={this.onChangeDeviceConfiguration}
                    onSave={this.onSaveDeviceConfiguration}
                    loading={!session?.deviceConfiguration?.document}
                    popup={true}
                />
                <DefaultTeleopConfiguration
                    devices={[device]}
                    document={session.deviceConfiguration?.document}
                    dirty={false}
                    validation={{}}
                    onChange={this.onChangeDeviceConfiguration}
                    onSave={this.onSaveDeviceConfiguration}
                    loading={!session?.deviceConfiguration?.document}
                    popup={true}
                />
                <>
                    <Fader visible={needsRestart()}>
                        <WarningMessage>
                            <div>
                                Stream configuration has changed. Restart the
                                session to apply the new settings{" "}
                                <TextButton
                                    onClicked={this.onSaveAndApplyConfiguration}
                                >
                                    Save and restart session now
                                </TextButton>
                            </div>
                        </WarningMessage>
                    </Fader>
                </>
                <HStack hAlign="end">
                    <Buttons>
                        <>
                            <StandardButton
                                subtle={true}
                                onClicked={this.onResetConfiguration}
                            >
                                Reset
                            </StandardButton>
                        </>
                        <StandardButton onClicked={this.onCancel} dark={true}>
                            Cancel
                        </StandardButton>
                        <StandardButton
                            onClicked={this.onSaveAndApplyConfiguration}
                            primary={true}
                            data-tooltip={
                                "Saving these changes will restart your teleoperation session"
                            }
                        >
                            Save
                        </StandardButton>
                    </Buttons>
                </HStack>
            </Popup>
        );
    }

    private needsRestart() {
        const { session } = operateStore;
        const originalConfig =
            session?.originalDeviceConfiguration?.document.teleop;
        const currentConfig = session?.deviceConfiguration?.document.teleop;

        if (!originalConfig || !currentConfig) {
            return false;
        }

        const original = clone(originalConfig);
        const current = clone(currentConfig);

        return (
            !equal(original.customStreams, current.customStreams) ||
            !equal(original.hardwareStreams, current.hardwareStreams) ||
            !equal(original.rosStreams, current.rosStreams)
        );
    }

    private onResetConfiguration = () => {
        const originalDeviceConfiguration =
            operateStore.session?.originalDeviceConfiguration;
        if (originalDeviceConfiguration) {
            this.onChangeDeviceConfiguration(
                originalDeviceConfiguration.document,
            );
        }
    };

    private onSaveConfiguration = () => {
        const { session } = operateStore;
        if (!session) {
            return;
        }
        const { deviceConfiguration } = session;
        if (!deviceConfiguration) {
            return;
        }
        session.updatedDeviceConfiguration = clone(deviceConfiguration);
        this.onStopConfiguringDevice();
    };

    private onSaveAndApplyConfiguration = async () => {
        const { session } = operateStore;
        if (!session) {
            return;
        }
        this.onSaveConfiguration();
        await session.updateDeviceConfiguration();
        await session.restart();
    };

    private onCancel = () => {
        const { session } = operateStore;
        if (!session) {
            return;
        }
        session.deviceConfiguration = session.originalDeviceConfiguration;
        session.updatedDeviceConfiguration = undefined;
        this.onStopConfiguringDevice();
    };

    private onStopConfiguringDevice = () => {
        operateStore.editingDevice = false;
    };

    private onChangeDeviceConfiguration = (
        document: IDeviceConfigurationDocument,
    ) => {
        const { session } = operateStore;
        if (!session) {
            return;
        }
        const { deviceConfiguration } = session;
        if (!deviceConfiguration) {
            return;
        }

        deviceConfiguration.document = document;
    };

    private onSaveDeviceConfiguration = () => {
        this.onSaveConfiguration();
    };
}
